// frontend/src/components/PWAManager.jsx
// Comprehensive PWA manager for install prompts, offline detection, and service worker updates

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const PWAManager = ({
  children,
  showInstallPrompt = true,
  showOfflineIndicator = true,
  hideBanners = false,
}) => {
  // Use a try-catch around useTranslation to handle initialization issues
  let t = null;
  try {
    const translation = useTranslation();
    t = translation.t;
  } catch (error) {
    console.warn("PWA: Translation hook error, using fallbacks:", error);
  }

  // Fallback function in case i18n is not ready
  const safeTranslate = (key, fallback) => {
    try {
      return t ? t(key, fallback) : fallback;
    } catch (error) {
      console.warn("PWA: Translation error, using fallback:", error);
      return fallback;
    }
  };
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [swUpdateAvailable, setSwUpdateAvailable] = useState(false);

  // Handle beforeinstallprompt event
  useEffect(() => {
    console.log("PWA: Setting up event listeners");
    console.log("PWA: Current state - isInstallable:", isInstallable, "showInstallBanner:", showInstallBanner, "deferredPrompt:", !!deferredPrompt);

    const handleBeforeInstallPrompt = (e) => {
      console.log("PWA: Before install prompt triggered");
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);

      // Show install banner after a delay
      setTimeout(() => {
        console.log("PWA: Showing install banner");
        setShowInstallBanner(true);
      }, 3000);
    };

    // Handle app installed event
    const handleAppInstalled = () => {
      console.log("PWA: App was installed");
      setIsInstallable(false);
      setShowInstallBanner(false);
      setDeferredPrompt(null);
    };

    // Handle online/offline status
    const handleOnline = () => {
      console.log("PWA: Back online");
      setIsOffline(false);
    };

    const handleOffline = () => {
      console.log("PWA: Gone offline");
      setIsOffline(true);
    };

    // Handle service worker updates
    const handleSWUpdate = () => {
      console.log("PWA: Service worker update available");
      setSwUpdateAvailable(true);
    };

    // Add event listeners
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Listen for service worker updates
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        console.log("PWA: Service worker controller changed");
      });

      // Check for updates
      navigator.serviceWorker.ready.then((registration) => {
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              handleSWUpdate();
            }
          });
        });
      });
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Handle install button click
  const handleInstallClick = async () => {
    console.log(
      "PWA: Install button clicked, deferredPrompt:",
      !!deferredPrompt
    );

    if (!deferredPrompt) {
      console.log("PWA: No deferred prompt available");
      return;
    }

    console.log("PWA: Installing app...");

    try {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;

      console.log("PWA: Install prompt outcome:", outcome);

      if (outcome === "accepted") {
        console.log("PWA: User accepted the install prompt");
      } else {
        console.log("PWA: User dismissed the install prompt");
      }
    } catch (error) {
      console.error("PWA: Error during install:", error);
    }

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setIsInstallable(false);
    setShowInstallBanner(false);
  };

  // Handle service worker update button click
  const handleSWUpdateClick = () => {
    console.log("PWA: Update button clicked");

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          console.log("PWA: Sending SKIP_WAITING message to service worker");
          registration.waiting.postMessage({ type: "SKIP_WAITING" });

          // Listen for the controllerchange event
          navigator.serviceWorker.addEventListener("controllerchange", () => {
            console.log("PWA: Controller changed, refreshing page");
            window.location.reload();
          });
        } else {
          console.log("PWA: No waiting service worker found");
          // Fallback: just refresh the page
          window.location.reload();
        }
      });
    } else {
      console.log("PWA: Service worker not supported, refreshing page");
      window.location.reload();
    }

    setSwUpdateAvailable(false);
  };

  // Dismiss install banner
  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
  };

  // Debug function to manually show banner (for testing)
  const debugShowBanner = () => {
    console.log("PWA: Debug - manually showing banner");
    setIsInstallable(true);
    setShowInstallBanner(true);

    // Create a mock deferred prompt for testing
    if (!deferredPrompt) {
      console.log("PWA: Creating mock deferred prompt for testing");
      const mockPrompt = {
        prompt: () => {
          console.log("PWA: Mock install prompt triggered");
          // Simulate user choice
          setTimeout(() => {
            console.log("PWA: Mock install accepted");
            setDeferredPrompt(null);
            setIsInstallable(false);
            setShowInstallBanner(false);
          }, 1000);
        },
        userChoice: Promise.resolve({ outcome: "accepted" }),
      };
      setDeferredPrompt(mockPrompt);
    }
  };

  // Expose debug function to window for testing (development only)
  useEffect(() => {
    if (import.meta.env.DEV && typeof window !== "undefined") {
      window.debugPWABanner = debugShowBanner;
      console.log("PWA: Debug function available - call window.debugPWABanner() to show banner");
    }
  }, []);

  // Debug function to manually show update banner (remove in production)
  const debugShowUpdateBanner = () => {
    console.log("PWA: Debug - manually showing update banner");
    setSwUpdateAvailable(true);
  };

  return (
    <>
      {children}

      {/* Install Banner */}
      {showInstallPrompt && showInstallBanner && isInstallable && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-5">
          <div className="flex items-start space-x-8">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center p-1">
                <span className="text-white text-sm">📱</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-gray-900 leading-tight">
                {safeTranslate("pwa.install_title", "Install Alertique")}
              </h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                {safeTranslate(
                  "pwa.install_description",
                  "Add to home screen for quick access"
                )}
              </p>
            </div>
            <button
              onClick={dismissInstallBanner}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-1 -mt-1 -mr-1"
            >
              <span className="text-xl">×</span>
            </button>
          </div>
          <div className="flex space-x-3 mt-4">
            <button
              onClick={handleInstallClick}
              className="flex-1 bg-blue-500 text-white text-sm font-medium py-3 px-4 rounded-md hover:bg-blue-600 transition-colors touch-target"
            >
              {safeTranslate("pwa.install_button", "Install")}
            </button>
            <button
              onClick={dismissInstallBanner}
              className="flex-1 bg-gray-100 text-gray-700 text-sm font-medium py-3 px-4 rounded-md hover:bg-gray-200 transition-colors touch-target"
            >
              {safeTranslate("pwa.later_button", "Later")}
            </button>
          </div>
        </div>
      )}

      {/* Offline Indicator */}
      {showOfflineIndicator && isOffline && !hideBanners && (
        <div className="fixed top-20 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-yellow-500 text-white rounded-lg shadow-lg z-50 p-3">
          <div className="flex items-center space-x-2">
            <span className="text-lg">📡</span>
            <span className="text-sm font-medium">
              {safeTranslate(
                "pwa.offline_message",
                "You're offline. Some features may be limited."
              )}
            </span>
          </div>
        </div>
      )}

      {/* Service Worker Update Banner */}
      {swUpdateAvailable && !hideBanners && (
        <div className="fixed top-32 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-green-500 text-white rounded-lg shadow-lg z-50 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-lg">🔄</span>
              <span className="text-sm font-medium">
                {safeTranslate("pwa.update_available", "Update available")}
              </span>
            </div>
            <button
              onClick={handleSWUpdateClick}
              className="bg-white text-green-500 text-xs font-medium py-1 px-2 rounded hover:bg-gray-100 transition-colors touch-target"
            >
              {safeTranslate("pwa.update_button", "Update")}
            </button>
          </div>
        </div>
      )}

    </>
  );
};

export default PWAManager;
